// Countdown.jsx
import { useEffect, useRef, useState } from "react";

export default function Countdown({
  seconds,
  isRunning = true,
  onComplete,
  format = "mm:ss",
  className,
  style
}) {
  const sanitize = (n) => (Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);

  const [remaining, setRemaining] = useState(() => sanitize(seconds));
  const remainingRef = useRef(remaining);
  const endTimeRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    remainingRef.current = remaining;
  }, [remaining]);

  useEffect(() => {
    const startVal = sanitize(seconds);
    setRemaining(startVal);
    endTimeRef.current = Date.now() + startVal * 1000;
  }, [seconds]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!isRunning || remainingRef.current <= 0) return;

    endTimeRef.current = Date.now() + remainingRef.current * 1000;

    intervalRef.current = window.setInterval(() => {
      const msLeft = Math.max(0, (endTimeRef.current ?? Date.now()) - Date.now());
      const next = Math.ceil(msLeft / 1000);
      setRemaining(next);

      if (next <= 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        onComplete && onComplete();
      }
    }, 200);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, onComplete]);

  const formatted =
    format === "seconds"
      ? String(remaining)
      : `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;

  return (
    <span role="timer" aria-live="polite" className={className} style={{...style}}>
      {formatted}
    </span>
  );
}
